import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserData, exportUserData, deleteAccountWithToken, requestDeletionToken, checkExportStatus, createPortalSession, getMySubscription, importCliptoData, importBookmarks, request, fetchSiteSettingsPublic } from '../services/api';
import { formatDate } from '../utils/format';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import useFeatureGate from '../hooks/useFeatureGate';
import { isOptedOut, setOptOut } from '../services/analytics';
import ImportResultsModal from '../components/ImportResultsModal';

export default function SettingsPage() {
  const [allowAnalytics, setAllowAnalytics] = useState(true);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [portalError, setPortalError] = useState(null);
  // Import state
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [showImportResults, setShowImportResults] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [pendingImportType, setPendingImportType] = useState(null);
  // Profile state
  const [profileOpen, setProfileOpen] = useState(true);
  const [profile, setProfile] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [showPublicProfiles, setShowPublicProfiles] = useState(true);
  const navigate = useNavigate();
  const stripeEnabled = useFeatureGate('stripe_payments');
  const importEnabled = useFeatureGate('feature:import');

  useEffect(() => {
    try {
      // allowAnalytics is the inverse of opted-out state
      setAllowAnalytics(!isOptedOut());
    } catch (e) {
      // ignore
    }
  }, []);

  // Load user's profile on mount
  useEffect(() => {
    let mounted = true;
    async function loadProfile() {
      try {
        const resp = await request('/api/user/profile');
        /* c8 ignore start */
        if (!mounted) return;
        setProfile(resp.profile);
        setIsPublic(resp.profile.is_public ?? resp.profile.isPublic ?? false);
        /* c8 ignore stop */
      } catch (e) {
        // ignore profile load errors
      }
    }
    loadProfile();
    // Fetch public site settings to decide if public profiles are enabled
    (async () => {
      try {
        let siteSettingsPromise;
        try {
          if (typeof fetchSiteSettingsPublic === 'function') {
            const maybe = fetchSiteSettingsPublic();
            if (maybe && typeof maybe.then === 'function') {
              siteSettingsPromise = maybe.catch(() => ({}));
            } else {
              siteSettingsPromise = Promise.resolve(maybe || {});
            }
          } else {
            /* c8 ignore next */
            siteSettingsPromise = Promise.resolve({});
          }
        } catch (e) {
          /* c8 ignore next */
          siteSettingsPromise = Promise.resolve({});
        }

        const s = await siteSettingsPromise;
        if (s && s.hasOwnProperty('publicProfile')) {
          const val = s.publicProfile;
        /* c8 ignore start */
        const enabled = val === true || val === 'true';
        /* c8 ignore stop */
          setShowPublicProfiles(enabled);
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load subscription info on mount
  useEffect(() => {
    let mounted = true;
    async function loadSubscription() {
      try {
        const resp = await getMySubscription();
        /* c8 ignore start */
        if (!mounted) return;
        /* c8 ignore stop */
        setSubscriptionInfo(resp.subscription);
      } catch (e) {
        // ignore — user may not be authenticated or endpoint unavailable
      } finally {
        /* c8 ignore start */
        if (mounted) setSubscriptionLoading(false);
        /* c8 ignore stop */
      }
    }
    loadSubscription();
    return () => { mounted = false; };
  }, []);

  // Poll export status when pending
  useEffect(() => {
    if (!exportStatus || exportStatus.status !== 'pending') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await checkExportStatus(exportStatus.exportId);
        const newStatus = response.export.status;
        setExportStatus(prev => ({
          ...prev,
          status: newStatus,
        }));
      /* c8 ignore next */
      } catch (err) {
        // Stop polling on error
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [exportStatus?.exportId, exportStatus?.status]);

  const handleUpdateProfile = async () => {
    setProfileError(null);
    setProfileSuccess(false);
    setProfileLoading(true);
    try {
      const response = await request('/api/user/profile', {
        method: 'PUT',
        body: JSON.stringify({ is_public: isPublic }),
      });
      setProfile(response.profile);
      /* c8 ignore start */
      setIsPublic(response.profile.is_public ?? response.profile.isPublic ?? false);
      /* c8 ignore stop */
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      /* c8 ignore start */
      setProfileError(err.message || 'Failed to update profile');
      /* c8 ignore stop */
    } finally {
      setProfileLoading(false);
    }
  };

  const handleViewProfile = () => {
    /* c8 ignore start */
    if (profile && profile.username) {
    /* c8 ignore stop */
      window.open(`/u/${profile.username}`, '_blank');
    }
  };

  const handleViewData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getUserData();
      setUserData(response.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalError(null);
    // Create a billing portal session and redirect
    try {
      const resp = await createPortalSession();
      if (resp && resp.url) {
        window.location.assign(resp.url);
      } else {
        setPortalError('Unable to open billing portal.');
      }
    } catch (err) {
      if (err && err.status === 401) {
        // Not authenticated — send user to login
        navigate('/login');
      } else {
        /* c8 ignore start */
        setPortalError(err.message || 'Unable to open billing portal');
        /* c8 ignore stop */
      }
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    setError(null);
    setExportStatus(null);
    try {
      const response = await exportUserData();
      setExportStatus({
        message: response.message,
        exportId: response.export.id,
        status: response.export.status,
        downloadToken: response.export.download_token,
      });
    } catch (err) {
      /* c8 ignore start */
      setError(err.response?.data?.error?.message || 'Failed to request data export');
      /* c8 ignore stop */
    } finally {
      setLoading(false);
    }
  };

  const handleImportFile = async (file, importType) => {
    /* c8 ignore start */
    if (!file) return;
    /* c8 ignore stop */
    
    setImportError(null);
    setPendingImportFile(file);
    setPendingImportType(importType);
  };

  const confirmImport = async () => {
    /* c8 ignore start */
    if (!pendingImportFile || !pendingImportType) return;
    /* c8 ignore stop */

    setImportLoading(true);
    setImportError(null);
    
    try {
      let response;
      if (pendingImportType === 'json') {
        response = await importCliptoData(pendingImportFile);
      /* c8 ignore start */
      } else if (pendingImportType === 'html') {
      /* c8 ignore stop */
        response = await importBookmarks(pendingImportFile);
      } else {
        /* c8 ignore start */
        throw new Error('Unknown import type');
        /* c8 ignore stop */
      }

      setImportResults({
        message: response.message,
        imported: response.imported,
        skipped: response.skipped,
        /* c8 ignore start */
        errors: response.errors || [],
        /* c8 ignore stop */
      });
      setShowImportResults(true);
      
      // Refresh user data to show updated counts
      try {
        const updatedData = await getUserData();
        setUserData(updatedData.data);
      } catch (e) {
        // ignore refresh failures
      }
    } catch (err) {
      const errorMessage = err.message || 'Import failed';
      
      if (err.status === 413) {
        setImportError('File is too large. Maximum file size is 10 MB.');
      } else if (err.status === 429) {
        setImportError('You can import a maximum of 3 files per hour. Please try again later.');
      } else if (err.status === 400) {
        /* c8 ignore start */
        setImportError(errorMessage || 'Invalid file format or data structure.');
        /* c8 ignore stop */
      } else {
        setImportError(errorMessage);
      }
    } finally {
      setImportLoading(false);
      setPendingImportFile(null);
      setPendingImportType(null);
    }
  };

  const cancelImport = () => {
    setPendingImportFile(null);
    setPendingImportType(null);
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    setError(null);
    setDeleteStatus(null);
    try {
      // Request a short-lived confirmation token (production requires it)
      const tokenResp = await requestDeletionToken();
      const token = tokenResp.confirmation_token;

      const response = await deleteAccountWithToken(token);
      setDeleteStatus({
        message: response.message,
      });
      setShowDeleteConfirm(false);
    } catch (err) {
      /* c8 ignore start */
      setError(err.message || 'Failed to delete account');
      /* c8 ignore stop */
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8"><span role="img" aria-label="Settings">⚙️</span> Account Settings</h1>

        {/* Profile Management moved below Subscription Management */}

        {/* Privacy & Analytics */}
        <section className="bg-white shadow-sm rounded-lg mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setPrivacyOpen(!privacyOpen)}
              aria-expanded={privacyOpen}
              className="w-full flex items-start justify-between text-left"
            >
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Privacy & Analytics</h2>
                <p className="mt-1 text-sm text-gray-500">Control anonymous usage analytics for this account.</p>
              </div>
              <svg className={`h-5 w-5 text-gray-500 transform transition-transform ${privacyOpen ? 'rotate-90' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          {privacyOpen && (
            <div className="px-6 py-5">
            <label className="inline-flex items-center space-x-3">
              <input
                type="checkbox"
                checked={allowAnalytics}
                onChange={(e) => {
                  const checked = e.target.checked;
                  // If checked, user allows analytics => opt-out should be false
                  try {
                    setOptOut(!checked);
                    setAllowAnalytics(checked);
                  } catch (err) {
                    // ignore
                  }
                }}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="text-sm text-gray-700">Allow anonymous usage analytics</span>
            </label>
            <p className="mt-2 text-xs text-gray-500">We collect anonymous usage data to improve the product. No personal data is sent.</p>
            </div>
          )}
        </section>

        

        {/* Subscription Management */}
        <section className="bg-white shadow-sm rounded-lg mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setSubscriptionOpen(!subscriptionOpen)}
              aria-expanded={subscriptionOpen}
              className="w-full flex items-start justify-between text-left"
            >
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Subscription Management</h2>
                <p className="mt-1 text-sm text-gray-500">Manage your plan, billing, and subscription details.</p>
              </div>
              <svg className={`h-5 w-5 text-gray-500 transform transition-transform ${subscriptionOpen ? 'rotate-90' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          {subscriptionOpen && (
            <div className="px-6 py-5">
              {subscriptionLoading ? (
                <div className="animate-pulse bg-gray-100 rounded h-24" />
              ) : (
                <div className="space-y-4">
                  {/* Current plan status */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Current plan</p>
                      <p className="text-base font-medium text-gray-900">
                        {subscriptionInfo?.tier_name ?? 'Free'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Status</p>
                      <p className="text-base font-medium text-gray-900 capitalize">
                        {subscriptionInfo?.status ?? 'Active'}
                      </p>
                    </div>
                    {subscriptionInfo?.current_period_end && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Next billing date</p>
                        <p className="text-base font-medium text-gray-900">
                          {formatDate(subscriptionInfo.current_period_end, 'date')}
                        </p>
                      </div>
                    )}
                  </div>

                  {portalError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                      {portalError}
                    </div>
                  )}

                  {/* Billing actions */}
                  {subscriptionInfo?.has_stripe_customer ? (
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                      <button
                        onClick={handleManageSubscription}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium transition-colors"
                      >
                        Manage subscription
                      </button>
                      <button
                        onClick={handleManageSubscription}
                        className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm transition-colors"
                      >
                        Cancel subscription
                      </button>
                      <span className="text-xs text-gray-500">Managed securely via Stripe</span>
                      {stripeEnabled !== false && (
                        <a href="/pricing" className="text-sm text-primary hover:underline ml-2">See pricing</a>
                      )}
                    </div>
                  ) : (
                    <div className="pt-2">
                      {stripeEnabled !== false && (
                          <>
                              <a
                                href="/pricing"
                                className="inline-block px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium transition-colors"
                              >
                                View plans &amp; upgrade
                              </a>
                              <p className="text-xs text-gray-500 mt-2">
                                You're on the free plan. <a href="/pricing" className="text-purple-600 hover:underline">See pricing</a> for details on paid tiers.
                              </p>
                            </>
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Public Profile (moved below Subscription Management) */}
        {showPublicProfiles && (
        <section className="bg-white shadow-sm rounded-lg mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setProfileOpen(!profileOpen)}
              aria-expanded={profileOpen}
              className="w-full flex items-start justify-between text-left"
            >
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Public Profile</h2>
                <p className="mt-1 text-sm text-gray-500">Manage your public profile and visibility.</p>
              </div>
              <svg className={`h-5 w-5 text-gray-500 transform transition-transform ${profileOpen ? 'rotate-90' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          {profileOpen && (
            <div className="px-6 py-5">
              {profileError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                  {profileError}
                </div>
              )}
              {profileSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                  Profile updated successfully!
                </div>
              )}
              
              {profile && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                      @{profile.username}
                    </p>
                  </div>

                  <div className="mb-6">
                    <label className="inline-flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                        className="form-checkbox h-5 w-5 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">
                        Make my profile publicly visible at <code>/u/{profile.username}</code>
                      </span>
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleUpdateProfile}
                      disabled={profileLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition"
                    >
                      {profileLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                    {isPublic && (
                      <button
                        onClick={handleViewProfile}
                        className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 transition"
                      >
                        View Public Profile
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </section>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm" role="alert">
            <p>{error}</p>
          </div>
        )}

        {/* Data Management Section */}
        <section className="bg-white shadow-sm rounded-lg mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setDataOpen(!dataOpen)}
              aria-expanded={dataOpen}
              className="w-full flex items-start justify-between text-left"
            >
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Data Management</h2>
                <p className="mt-1 text-sm text-gray-500">View, export, or delete your personal data</p>
              </div>
              <svg className={`h-5 w-5 text-gray-500 transform transition-transform ${dataOpen ? 'rotate-90' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {dataOpen && (
            <div className="px-6 py-5 space-y-6">
            {/* View Data */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">View Your Data</h3>
              <p className="text-sm text-gray-600 mb-3">
                See all the data we have collected about you.
              </p>
              <button
                onClick={handleViewData}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading && !userData ? 'Loading...' : 'View My Data'}
              </button>

              {userData && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">Your Data Summary</h4>
                  <dl className="grid grid-cols-1 gap-2 text-sm">
                    <div>
                      <dt className="font-medium text-gray-700">Username:</dt>
                      {/* c8 ignore start */}
                      <dd className="text-gray-600">{userData.profile.display_name || userData.profile.twitch_username}</dd>
                      {/* c8 ignore stop */}
                    </div>
                    <div>
                      <dt className="font-medium text-gray-700">Email:</dt>
                      <dd className="text-gray-600">{userData.profile.email}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-700">Account Created:</dt>
                      <dd className="text-gray-600">
                        {formatDate(userData.profile.created_at, 'date')}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-700">Collection Clips:</dt>
                      <dd className="text-gray-600">{userData.statistics.collection_clips}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-700">Favorite Channels:</dt>
                      <dd className="text-gray-600">{userData.statistics.favorite_channels}</dd>
                    </div>
                    {userData.subscription && (
                      <div>
                        <dt className="font-medium text-gray-700">Subscription:</dt>
                        <dd className="text-gray-600">
                          {/* c8 ignore start */}
                          {userData.subscription.tier_id == null || userData.subscription.tier_name === 'Unknown'
                            ? 'Free'
                            : (userData.subscription.tier_name || 'Unknown')}
                          {/* c8 ignore stop */}
                        </dd>
                      </div>
                    )}
                    {/* Billing actions */}
                    {userData.subscription && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Billing</h4>
                        {userData.subscription.stripe_customer_id ? (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={handleManageSubscription}
                              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                            >
                              Manage Subscription
                            </button>
                            <div className="flex flex-col text-sm text-gray-500">
                              <span>Manage payments and billing in Stripe</span>
                              {/* c8 ignore start */}
                              {(
                                userData.subscription.current_period_end || subscriptionInfo?.current_period_end
                              ) && (
                                <span>
                                  Renews on {formatDate(userData.subscription.current_period_end || subscriptionInfo.current_period_end, 'date')}
                                </span>
                              )}
                              {/* c8 ignore stop */}
                            </div>
                          </div>
                        ) : (
                          <div>
                            {stripeEnabled !== false && (
                              <a href="/pricing" className="inline-flex px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">Upgrade Plan</a>
                            )}
                            <p className="text-sm text-gray-500 mt-2">You don't have a billing profile yet. Upgrade to create one.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>

            {/* Export Data */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Export Your Data</h3>
              <p className="text-sm text-gray-600 mb-3">
                Download all your personal data. You can request one export per hour.
              </p>
              <button
                onClick={handleExportData}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading && !exportStatus ? 'Generating...' : 'Export My Data'}
              </button>

              {exportStatus && (
                <div className={`mt-4 p-4 rounded-md border ${
                  exportStatus.status === 'completed'
                    ? 'bg-green-50 border-green-200'
                    : exportStatus.status === 'failed'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className={`font-medium ${
                        exportStatus.status === 'completed'
                          ? 'text-green-800'
                          : exportStatus.status === 'failed'
                          ? 'text-red-800'
                          : 'text-blue-800'
                      }`}>
                        {exportStatus.status === 'completed'
                          ? '✓ Your data export is ready for download!'
                          : exportStatus.status === 'failed'
                          ? 'Failed to generate your data export'
                          : 'Generating your data export...'}
                      </p>
                      <p className={`text-sm mt-1 ${
                        exportStatus.status === 'completed'
                          ? 'text-green-700'
                          : exportStatus.status === 'failed'
                          ? 'text-red-700'
                          : 'text-blue-700'
                      }`}>
                        Export ID: {exportStatus.exportId}
                      </p>
                    </div>
                    {exportStatus.status !== 'completed' && (
                      <div className="animate-spin">
                        <svg className="w-5 h-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  <p className={`text-sm mb-3 ${
                    exportStatus.status === 'completed'
                      ? 'text-green-700'
                      : exportStatus.status === 'failed'
                      ? 'text-red-700'
                      : 'text-blue-700'
                  }`}>
                    {exportStatus.status === 'completed' && 'Your JSON data export includes your profile, collection, subscriptions, and full clip information.'}
                    {exportStatus.status === 'failed' && 'Please try again or contact support.'}
                  </p>

                  {exportStatus.status === 'completed' && exportStatus.downloadToken && (
                    <a
                      href={`/api/exports/${exportStatus.exportId}/download?token=${exportStatus.downloadToken}`}
                      download
                      className="inline-flex px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      📥 Download Export
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Import Data */}
            {importEnabled && (
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Import Data</h3>
              <p className="text-sm text-gray-600 mb-4">
                Restore data from a Clipto export or import browser bookmarks. You can import up to 3 files per hour.
              </p>

              {importError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">⚠️ {importError}</p>
                </div>
              )}

              {pendingImportFile && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800 mb-3">
                    Ready to import <strong>{pendingImportFile.name}</strong> ({(pendingImportFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={confirmImport}
                      disabled={importLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {importLoading ? 'Importing...' : 'Confirm Import'}
                    </button>
                    <button
                      onClick={cancelImport}
                      disabled={importLoading}
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 disabled:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Import Clipto JSON */}
                <div className="p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <h4 className="font-medium text-gray-900 mb-2">📋 Clipto Export</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Import a .json export file from your Clipto account backup.
                  </p>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      /* c8 ignore start */
                      if (file) handleImportFile(file, 'json');
                      /* c8 ignore stop */
                    }}
                    disabled={importLoading || !!pendingImportFile}
                    className="hidden"
                    id="clipto-import-input"
                  />
                  <label
                    htmlFor="clipto-import-input"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 cursor-pointer"
                  >
                    {/* c8 ignore start */}
                    {importLoading && pendingImportType === 'json' ? 'Importing...' : 'Import JSON'}
                    {/* c8 ignore stop */}
                  </label>
                </div>

                {/* Import Browser Bookmarks */}
                <div className="p-4 border border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <h4 className="font-medium text-gray-900 mb-2">🔖 Browser Bookmarks</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Import bookmarks from Chrome, Firefox, Brave, Safari, or Edge (.html file).
                  </p>
                  <input
                    type="file"
                    accept=".html,.htm"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      /* c8 ignore start */
                      if (file) handleImportFile(file, 'html');
                      /* c8 ignore stop */
                    }}
                    disabled={importLoading || !!pendingImportFile}
                    className="hidden"
                    id="bookmarks-import-input"
                  />
                  <label
                    htmlFor="bookmarks-import-input"
                    className="inline-block px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 cursor-pointer"
                  >
                    {/* c8 ignore start */}
                    {importLoading && pendingImportType === 'html' ? 'Importing...' : 'Import Bookmarks'}
                    {/* c8 ignore stop */}
                  </label>
                </div>
              </div>

              {/* Help text */}
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-xs text-gray-600 font-semibold mb-2">How to export browser bookmarks:</p>
                <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                  <li>Chrome/Brave: Settings → Bookmarks → Bookmark Manager (⚙️) → Export</li>
                  <li>Firefox: Bookmarks → Manage (⋯) → Import and Backup → Export</li>
                  <li>Safari: File → Export Bookmarks</li>
                </ul>
              </div>
            </div>
            )}

            {/* Support the Project (Donation) */}
            {/* c8 ignore start */}
            {import.meta.env.VITE_STRIPE_DONATION_LINK && (
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Support the Project</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Enjoying Clipto? Consider supporting development with a small donation.
                </p>
                <a
                  href={import.meta.env.VITE_STRIPE_DONATION_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                >
                  ❤️ Donate
                </a>
              </div>
            )}
            {/* c8 ignore stop */}

            {/* Delete Account */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-red-900 mb-2">Delete Account</h3>
              <p className="text-sm text-gray-600 mb-3">
                Permanently delete your account and all associated data immediately. This action cannot be undone.
              </p>

              {!deleteStatus && !showDeleteConfirm && (
                <div>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Delete My Account
                  </button>
                </div>
              )}

              {showDeleteConfirm && (
                <div className="p-6 bg-gray-900 text-gray-100 rounded-md border border-gray-700">
                  <p className="text-white font-medium mb-2">Are you sure?</p>
                  <p className="text-sm text-gray-300 mb-4">
                    This will permanently delete your account and all associated data immediately. This action cannot be undone.
                  </p>
                  <div>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                    >
                      {loading ? 'Deleting...' : 'Yes, Delete Account'}
                    </button>

                    <div className="mt-3">
                      <button
                        onClick={() => navigate('/dashboard')}
                        disabled={loading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Return to Clipto
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {deleteStatus && (
                <div className="p-4 bg-green-50 rounded-md border border-green-200">
                  <p className="text-green-900 font-medium mb-2">{deleteStatus.message}</p>
                  <p className="text-sm text-green-800">
                    Account deleted successfully.
                  </p>
                </div>
              )}
            </div>
          </div>
          )}
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <div className="flex justify-end">
            <button
              onClick={() => navigate('/dashboard')}
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Return to Clipto
            </button>
          </div>
        </div>
      </div>
      <ImportResultsModal 
        results={importResults} 
        isOpen={showImportResults} 
        onClose={() => setShowImportResults(false)}
      />
      </div>
      <SiteFooter />
    </div>
  );
}
